import Vue from 'vue'
import Vuex from 'vuex'
import axios from 'axios'
import notification from '@/store/modules/notification'
import tracking from '@/store/modules/tracking'
import api from '../utils/api'
import i18n from '@/plugins/i18n'

Vue.use(Vuex)
function validEmail(identifier) {
  return String(identifier)
    .toLowerCase()
    .match(
      /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
    )
}
function validPhone(identifier) {
  return String(identifier).match(/^\+?[1-9]\d{7,14}$/)
}
function identifierType(identifier) {
  if (validEmail(identifier)) {
    return 'email'
  }

  if (validPhone(identifier)) {
    return 'phone'
  }

  return undefined
}

/**
 * Handle the post-authentication redirect
 * This function will always redirect
 */
const LOGIN_PREF_KEY = 'guardian_login_preference'

function saveLoginPreference({ method, identifier }) {
  try {
    const raw = localStorage.getItem(LOGIN_PREF_KEY)
    const stored = raw ? JSON.parse(raw) : {}
    if (method) stored.method = method
    if (identifier) stored.preferredIdentifier = identifier
    localStorage.setItem(LOGIN_PREF_KEY, JSON.stringify(stored))
  } catch (_) {
    // ignore storage errors
  }
}

function handlePostAuthRedirect() {
  const params = new URLSearchParams(window.location.search)
  let redirect =
    params.get('client_id') && params.get('redirect_uri')
      ? `/authorize${window.location.search}`
      : `/login/error?error=invalid_request&error_description=Generic`

  if (params.get('is_saml') === 'true') {
    redirect = params.get('redirect_uri')
  }
  window.location = redirect
}

export default new Vuex.Store({
  state: {
    loading: false,
    showIdentifier: 'phone',
    showPassword: false,
    branding: null,
    stage: 'userIdentifier',
    preferredLoginMethod: null,
    preferredIdentifier: null,
    emailIdentifier: null,
    phoneIdentifier: null,
    message: null,
    password: null,
    user: {
      email: null,
      phone: null,
    },
    mfaChallengeToken: null,
    urlParam: null,
    qrCode: null,
    notFound: false,
    codeType: null,
    tempCode: null,
    loginHint: {
      value: null,
      type: null,
    },
    safetyAssistant: false,
  },
  getters: {
    brandingCompany: (state) => ({
      name: state.branding?.companyName || null,
      logo: state.branding?.logo || null,
    }),
    brandingEnabled: (state) =>
      state.branding?.brandedOnboardingEnabled || false,
    codeRecipient: (state) => {
      if (state.codeType === 'phone') {
        return state.phoneIdentifier
      }
      return state.emailIdentifier
    },
    qrCode: (state) => state.qrCode,
    qrType: (state) => state.branding?.entity,
    safetyAssistant: (state) => state.safetyAssistant,
    site: (state) => ({
      name: state.branding?.siteName || null,
    }),
  },
  actions: {
    loadPreferredLoginMethod({ commit, state }) {
      let method = null
      let preferredIdentifier = null

      try {
        const raw = localStorage.getItem(LOGIN_PREF_KEY)
        if (raw) {
          const stored = JSON.parse(raw)
          method = stored.method || null
          preferredIdentifier = stored.preferredIdentifier || null
        }
      } catch (_) {
        // ignore parse errors
      }

      commit('SET_PREFERRED_LOGIN_METHOD', method)
      commit('SET_PREFERRED_IDENTIFIER', preferredIdentifier)
      const hasLoginHint = state.loginHint?.type && state.loginHint?.value
      if (
        !hasLoginHint &&
        (preferredIdentifier === 'email' || preferredIdentifier === 'phone')
      ) {
        commit('SET_SHOW_IDENTIFIER', preferredIdentifier)
      }
    },
    async discover({ commit, getters, dispatch, state }, identifier) {
      commit('SET_AUTH_LOADING', true)
      commit('SET_NOT_FOUND', false)
      commit('SET_MESSAGE', null)
      const type = identifierType(identifier)
      if (!['phone', 'email'].includes(type)) {
        const message = getters.safetyAssistant
          ? i18n.t('common.pleaseEnterValidEmail')
          : i18n.t('common.pleaseEnterValidEmailOrPhone')

        commit('SET_AUTH_LOADING', false)
        commit('SET_MESSAGE', message)
        return
      }

      saveLoginPreference({ identifier: type })

      // Set client_id to be used for code request
      const params = new URLSearchParams(window.location.search)
      const clientId = params.get('client_id')
      // Set Identifier to be used for password or code
      const commitFunction =
        type === 'phone' ? 'SET_PHONE_IDENTIFIER' : 'SET_EMAIL_IDENTIFIER'
      commit(commitFunction, identifier)

      try {
        const encoded = encodeURIComponent(identifier)
        const { ok, data } = await api.get(
          `/authn/discover?userIdentifier=${encoded}`,
        )
        // perform redirect if needed
        if (data.isRedirect) {
          window.location = data.redirectUrl
          return
        }
        // If user is found
        const userFound = ok && data.userFound
        if (userFound) {
          commit('SET_USER', data)
          if (state.preferredLoginMethod === 'password') {
            commit('SET_STAGE', 'password')
          } else {
            await dispatch('requestCode', { type, client_id: clientId })
          }
          dispatch(
            'tracking/trackAmplitude',
            { name: 'user_found' },
            { root: true },
          )
        }
        // if user is not found
        if (!userFound) {
          await dispatch('requestTempCode', { identifier })
        }
        commit('SET_AUTH_LOADING', false)
      } catch (err) {
        commit('SET_NOT_FOUND', true)
        commit('SET_AUTH_LOADING', false)
        throw err
      }
    },
    async login({ commit, dispatch }, { email, password, phone }) {
      try {
        commit('SET_AUTH_LOADING', true)
        const res = await axios.post(`/authn/login`, {
          email,
          password,
          phone,
        })

        if (res.data.mfaRequired) {
          commit('SET_MFA_CHALLENGE_TOKEN', res.data.mfaChallengeToken)
          commit('SET_STAGE', 'enterOtp')
          commit('SET_AUTH_LOADING', false)
          return
        }

        if (res.data.isSuccess) {
          saveLoginPreference({
            method: 'password',
            identifier: email ? 'email' : 'phone',
          })
          await dispatch(
            'tracking/trackAmplitude',
            {
              name: 'logged_in',
              options: {
                identifier: email ? 'email' : 'phone',
                type: 'password',
                mfa: false,
              },
            },
            { root: true },
          )
          await dispatch('tracking/flushEvents')
          handlePostAuthRedirect()
        } else {
          dispatch(
            'notification/error',
            i18n.t('notifications.failedParseLogin'),
            {
              root: true,
            },
          )
        }
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        commit('SET_AUTH_LOADING', false)
      }
    },
    async loginWithCode({ commit, dispatch, state }, { code }) {
      try {
        commit('SET_AUTH_LOADING', true)
        const res = await axios.post(`/authn/login/code`, { code })
        if (res.data.mfaRequired) {
          commit('SET_MFA_CHALLENGE_TOKEN', res.data.mfaChallengeToken)
          commit('SET_STAGE', 'enterOtp')
          commit('SET_AUTH_LOADING', false)
          return
        }
        if (res.data.isSuccess) {
          saveLoginPreference({
            method: 'code',
            identifier: state.codeType === 'phone' ? 'phone' : 'email',
          })
          await dispatch(
            'tracking/trackAmplitude',
            {
              name: 'logged_in',
              options: {
                identifier: state.codeType === 'phone' ? 'phone' : 'email',
                type: 'code',
                mfa: false,
              },
            },
            { root: true },
          )
          await dispatch('tracking/flushEvents')
          handlePostAuthRedirect()
        } else {
          dispatch(
            'notification/error',
            i18n.t('notifications.failedParseLogin'),
            {
              root: true,
            },
          )
        }
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        commit('SET_AUTH_LOADING', false)
        throw err
      }
    },
    async verifyOtp({ commit, dispatch, state }, { otpToken }) {
      try {
        commit('SET_AUTH_LOADING', true)
        const res = await axios.post('/authn/verify-otp', {
          mfaChallengeToken: state.mfaChallengeToken,
          otpToken,
        })
        if (res.data.isSuccess) {
          await dispatch('tracking/flushEvents')
          handlePostAuthRedirect()
        } else {
          dispatch(
            'notification/error',
            i18n.t('notifications.failedParseLogin'),
            { root: true },
          )
        }
      } catch (err) {
        if (err.response && err.response.status === 403) {
          commit('SET_MFA_CHALLENGE_TOKEN', null)
          commit('SET_STAGE', 'userIdentifier')
          commit('SET_AUTH_LOADING', false)
          dispatch('notification/error', i18n.t('enterOtp.challengeExpired'), {
            root: true,
          })
          return
        }
        dispatch('notification/error', err, { root: true })
        commit('SET_AUTH_LOADING', false)
        throw err
      }
    },
    async requestReset({ dispatch, commit }, { identifier }) {
      try {
        const type = identifierType(identifier)
        if (!['phone', 'email'].includes(type)) {
          commit('SET_MESSAGE', i18n.t('common.pleaseEnterValidEmailOrPhone'))
          return false
        }
        if (type === 'phone') {
          await axios.post(`/authn/reset-request`, { phone: identifier })
        } else {
          await axios.post(`/authn/reset-request`, { email: identifier })
        }
        dispatch('notification/info', i18n.t('notifications.resetLinkSent'), {
          root: true,
        })
        return true
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        throw err
      }
    },
    async postReset({ dispatch }, { password, confirmPassword, token }) {
      try {
        await axios.post(`/authn/reset/${token}`, { password, confirmPassword })
        dispatch(
          'notification/info',
          i18n.t('notifications.passwordHasBeenSet'),
          { root: true },
        )
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        throw err
      }
    },
    async requestCode({ dispatch, commit }, { type, client_id }) {
      try {
        await axios.post(`/authn/request-code`, { type, client_id })
        commit('SET_STAGE', 'enterCode')
        commit('SET_CODE_TYPE', type)
        dispatch(
          'tracking/trackAmplitude',
          {
            name: 'code_sent',
            options: { type },
          },
          { root: true },
        )
        dispatch(
          'notification/info',
          i18n.t('notifications.codeSentTo', {
            type: i18n.t('common.' + type),
          }),
          {
            root: true,
          },
        )
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        throw err
      }
    },
    async requestTempCode({ dispatch, commit }, { identifier }) {
      try {
        const encoded = encodeURIComponent(identifier)
        const { ok, data } = await api.get(
          `/authn/request-code-temp?userIdentifier=${encoded}`,
        )
        if (ok) {
          const type = identifierType(identifier)
          commit('SET_STAGE', 'enterTempCode')
          commit('SET_CODE_TYPE', type)
          dispatch(
            'tracking/trackAmplitude',
            {
              name: 'code_sent',
              options: { type },
            },
            { root: true },
          )
          dispatch(
            'notification/info',
            i18n.t('notifications.codeSentTo', {
              type: i18n.t('common.' + type),
            }),
            {
              root: true,
            },
          )
        } else {
          let errorMessage = i18n.t('notifications.failedRequestCode')
          if (data?.type === 'COOLDOWN') {
            errorMessage = i18n.t('notifications.codeCooldown')
          }
          if (
            ['RATE_LIMIT_EXCEEDED', 'TEMPORARY_UNAVAILABLE'].includes(
              data?.type,
            )
          ) {
            errorMessage = i18n.t('notifications.tryAgainLater')
          }
          dispatch('notification/error', errorMessage, { root: true })
        }
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        throw err
      }
    },
    async requestPortalUrl() {
      const res = await axios.get(`/authn/discover/portal`)
      return res.data
    },
    async initSession({ commit }) {
      const queryParams = new URLSearchParams(window.location.search)
      const queryString = queryParams.toString()

      if (!queryString) {
        return
      }

      const url = `/authn/init-session?${queryString}`
      await axios.get(url).catch(() => {})
    },
    /**
     * This function is used to create a new user and automatically log that user in
     * @param {Object} context
     * @param {function} context.dispatch
     * @param {Object} context.state
     * @param {Object} createPayload
     * @param {string} createPayload.email
     * @param {string} createPayload.phone
     * @param {string} createPayload.firstName
     * @param {string} createPayload.lastName
     */
    async createAndLogin({ dispatch, state }, createPayload) {
      try {
        const { ok, status, data } = await api.post(`/authn/account/code`, {
          ...createPayload,
          code: state.tempCode,
        })
        if (!ok) {
          if (status === 400) {
            let errorMessage = data.message
            if (Array.isArray(data.message)) {
              errorMessage = data.message.join('\n')
            }
            dispatch('setMessage', errorMessage)
          }
          return
        }
        await dispatch(
          'tracking/trackAmplitude',
          { name: 'user_created' },
          { root: true },
        )
        await dispatch(
          'tracking/trackAmplitude',
          { name: 'logged_in' },
          { root: true },
        )
        await dispatch('tracking/flushEvents')
        // Account created and successfully authenticated
        handlePostAuthRedirect()
      } catch (err) {
        const message =
          err && err.response && err.response.data
            ? err.response.data.message
            : err.message || err
        dispatch('setMessage', message)
        throw err
      }
    },
    async checkTempCode({ commit, dispatch, state }, { code }) {
      try {
        commit('SET_AUTH_LOADING', true)
        commit('SET_TEMP_CODE', null)
        const { ok } = await api.post(`/authn/verify-code-temp`, { code })
        if (ok) {
          commit('SET_TEMP_CODE', code)
          commit('SET_STAGE', 'createAndLogin')
        } else {
          dispatch('notification/error', i18n.t('notifications.invalidCode'), {
            root: true,
          })
        }
      } catch (err) {
        dispatch('notification/error', err, { root: true })
        commit('SET_AUTH_LOADING', false)
        commit('SET_TEMP_CODE', null)
        throw err
      }
    },
    changeEmail({ commit }) {
      commit('SET_SHOW_PASSWORD', false)
    },
    setShowIdentifier({ commit }, identifier) {
      commit('SET_SHOW_IDENTIFIER', identifier)
    },
    setStage({ commit }, stage) {
      commit('SET_STAGE', stage)
    },
    setMessage({ commit }, message) {
      commit('SET_MESSAGE', message)
    },
    async loadBranding({ commit, dispatch }, qrCode) {
      commit('SET_BRANDING', null)
      try {
        const { ok, data } = await api.get(`/branding/qr/${qrCode}`)
        if (ok) {
          commit('SET_BRANDING', data)
        }
      } catch (_) {
        dispatch(
          'notification/error',
          i18n.t('notifications.failedFetchQrCode'),
          {
            root: true,
          },
        )
      }
    },
    resetState({ commit, state }) {
      commit('SET_AUTH_LOADING', true)
      commit('SET_STAGE', 'userIdentifier')
      commit('SET_USER', {})
      commit('SET_EMAIL_IDENTIFIER', null)
      commit('SET_PHONE_IDENTIFIER', null)
      commit('SET_MESSAGE', null)
      commit('SET_NOT_FOUND', false)
      commit('SET_CODE_TYPE', null)
      commit('SET_MFA_CHALLENGE_TOKEN', null)
      // Preserve existing loginHint if it exists
      commit('SET_LOGIN_HINT', {
        hint: state.loginHint.value || null,
        type: state.loginHint.type || null,
      })
      commit('SET_AUTH_LOADING', false)
    },
    setURLParam({ commit }, queryParam) {
      commit('SET_URL_PARAM', queryParam)
      const params = new URLSearchParams(queryParam)
      const qrCode = params.get('qr')
      const safetyAssistant = params.get('safety_assistant')

      commit('SET_QR_CODE', qrCode)
      commit('SET_SAFETY_ASSISTANT', safetyAssistant)
    },
    setLoginHint({ commit }, value) {
      const type = identifierType(value)
      commit('SET_LOGIN_HINT', { value, type })
      if (type === 'email' || type === 'phone') {
        commit('SET_SHOW_IDENTIFIER', type)
      }
    },
  },
  mutations: {
    SET_AUTH_LOADING(state, isLoading) {
      state.loading = isLoading
    },
    SET_SHOW_IDENTIFIER(state, identifier) {
      state.showIdentifier = identifier
    },
    SET_SHOW_PASSWORD(state, showPassword) {
      state.showPassword = showPassword
    },
    SET_STAGE(state, stage) {
      state.stage = stage
    },
    SET_PREFERRED_LOGIN_METHOD(state, method) {
      state.preferredLoginMethod = method
    },
    SET_PREFERRED_IDENTIFIER(state, identifier) {
      state.preferredIdentifier = identifier
    },
    SET_USER(state, user) {
      state.user = Object.assign(
        {},
        {
          name: user.name,
        },
      )
    },
    SET_MFA_CHALLENGE_TOKEN(state, token) {
      state.mfaChallengeToken = token
    },
    SET_EMAIL_IDENTIFIER(state, identifier) {
      state.emailIdentifier = identifier
    },
    SET_PHONE_IDENTIFIER(state, identifier) {
      state.phoneIdentifier = identifier
    },
    SET_MESSAGE(state, message) {
      state.message = message
    },
    SET_BRANDING(state, branding) {
      state.branding = branding
    },
    SET_TEMP_CODE(state, code) {
      state.tempCode = code
    },
    SET_QR_CODE(state, qrCode) {
      state.qrCode = qrCode
    },
    SET_URL_PARAM(state, queryParam) {
      state.urlParam = queryParam
    },
    SET_NOT_FOUND(state, notFound) {
      state.notFound = notFound
    },
    SET_CODE_TYPE(state, type) {
      state.codeType = type
    },
    SET_PASSWORD(state, password) {
      state.password = password
    },
    SET_VERIFICATION_CODE(state, code) {
      state.verificationCode = code
    },
    SET_LOGIN_HINT(state, { value, type }) {
      state.loginHint = { value, type }
    },
    SET_SAFETY_ASSISTANT(state, safetyAssistant) {
      state.safetyAssistant = !!safetyAssistant
    },
  },
  modules: {
    notification,
    tracking,
  },
})
